// src/components/loginpage/resetpassword/ResetPassword.jsx
//
// The page the reset email links to: https://riftworks.tech/reset-password?token=...
// Reads the token from the URL, enforces the v2 password policy client-side,
// then POSTs { token, new_password } and sends the user back to login.
//
// Logic, endpoints and error mapping unchanged. Markup rebuilt to the Modernist
// mockup: video panel with the brand plate, watermark and statement on the
// left; the reset form in a bordered card on the right.
import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import '../Login.css';
import './ResetPassword.css';
import Notifier from '../notifier/Notifier';
import VideoMask from '../../landingpage/VideoMask';
import { ReactComponent as LoginSvg } from '../res/login-graphic.svg';
import logoMark from '../../../assets/images/logo-mark.png';
import { PASSWORD_RULES, isPasswordValid } from '../signupbox/signupvalidator';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { kq } from '../../../config/routes';

// Reasons that mean "this link is no good" — these get a
// "request a new reset link" action instead of a plain error.
const TOKEN_REASONS = ['invalid_token', 'token_expired', 'missing_token'];

const mapResetError = (data, resp) => {
  const reason = data && (data.reason || data.error || data.message);

  switch (reason) {
    case 'invalid_token':
      return 'This reset link is invalid. Please request a new one.';
    case 'token_expired':
      return 'This reset link has expired. Please request a new one.';
    case 'missing_token':
      return 'This reset link is incomplete. Please request a new one.';
    case 'password_too_short':
      return 'Password must be at least 12 characters.';
    case 'password_too_long':
      return 'Password must be 20 characters or fewer.';
    case 'password_needs_uppercase':
      return 'Password must contain at least 1 uppercase letter.';
    case 'password_needs_lowercase':
      return 'Password must contain at least 1 lowercase letter.';
    case 'password_needs_number':
      return 'Password must contain at least 1 number.';
    case 'password_needs_special':
      return 'Password must contain at least 1 special character.';
    case 'server_error':
      return 'Something went wrong. Please try again.';
    default:
      break;
  }

  if (typeof reason === 'string') return `Reset failed: ${reason}`;
  if (resp && !resp.ok) return `Reset failed with status ${resp.status}`;
  return 'Reset failed. Unexpected server response.';
};

const EyeIcon = ({ open }) =>
  open ? (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M2 2l20 20" />
      <path d="M10.6 10.6a2 2 0 002.8 2.8" />
      <path d="M9.4 5.2A9.6 9.6 0 0112 5c5 0 9 4.4 10 7a15 15 0 01-3.5 4.5" />
      <path d="M6.2 6.6C3.9 8.1 2.5 10.2 2 12c1 2.6 5 7 10 7a9.7 9.7 0 004.3-1" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

// `area` keeps the way back inside the door the emailed link came through
// (Separation contract v2.1 §6): a Kuestion reset returns to the Kuestion sign-in.
const ResetPassword = ({ area = 'suite' }) => {
  const loginPath = area === 'kuestion' ? kq('login') : '/login?suite=1';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  // Passwords are never trimmed — the backend doesn't trim either, and a
  // leading/trailing space is a legal special character (contract §5).
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Set when the backend rejects the token, so we swap the form for a
  // "request a new link" screen rather than letting them retype.
  const [tokenDead, setTokenDead] = useState(false);

  const [note, setNote] = useState({ msg: '', type: 'info', signal: 0 });
  const sigRef = useRef(0);

  // Notifier keys off a strictly increasing signal; the next-tick schedule
  // keeps rapid-fire calls from being batched into a single render.
  const onNotify = (msg, type = 'info') => {
    const next = (sigRef.current += 1);
    setTimeout(() => setNote({ msg, type, signal: next }), 0);
  };

  const policyOk = useMemo(() => isPasswordValid(password), [password]);
  const matches = confirm.length > 0 && password === confirm;
  const canSubmit = !!token && policyOk && matches && !isSubmitting;

  const goToForgot = () => navigate(`${loginPath}${loginPath.includes('?') ? '&' : '?'}forgot=1`, { replace: true });

  const handleSubmit = async () => {
    if (isSubmitting) return;

    // Never call the API with an empty token (contract §3).
    if (!token) {
      onNotify('This reset link is invalid. Please request a new one.', 'error');
      return;
    }
    if (!policyOk) {
      onNotify('Please meet all password requirements.', 'warn');
      return;
    }
    if (!matches) {
      onNotify('Passwords do not match.', 'warn');
      return;
    }

    try {
      setIsSubmitting(true);

      const resp = await fetch(API_ENDPOINTS.RESET_PASSWORD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });

      let data = null;
      try {
        data = await resp.json();
      } catch {
        // response might not be JSON; ignore parse error
      }

      if (!resp.ok || !data || data.status !== 'success') {
        const reason = data && (data.reason || data.error || data.message);
        if (TOKEN_REASONS.includes(reason)) setTokenDead(true);
        onNotify(mapResetError(data, resp), 'error');
        return;
      }

      // Success → login page, which shows the alert off ?reset=1.
      onNotify('Password has been reset.', 'success');
      setTimeout(() => navigate(`${loginPath}${loginPath.includes('?') ? '&' : '?'}reset=1`, { replace: true }), 800);
    } catch (err) {
      console.error('Reset password error:', err);
      onNotify('Network or server error. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && canSubmit) handleSubmit();
  };

  // No token in the URL, or the backend rejected it — there is nothing to
  // submit, so offer a fresh link instead of a form.
  const linkBroken = !token || tokenDead;

  return (
    <div className="login-container">
      <div className="maincolleft">
        <VideoMask />

        <Link to="/" className="rw-brand-plate" title="Back to Riftworks home">
          <img src={logoMark} alt="Riftworks" />
          <span>
            <strong>RIFTWORKS</strong>
            <em>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Back to home
            </em>
          </span>
        </Link>

        <div className="innerwrapper1">
          <LoginSvg className="login-svg" />
        </div>

        <div className="login-statement">
          <h2>Reset links last 24 hours.</h2>
          <p>
            Each link works once. If it expires, request a new one from the login
            page and we&rsquo;ll email a fresh link.
          </p>
        </div>
      </div>

      <div className="maincolright">
        <div className="resetbox">
          {linkBroken ? (
            <>
              <span className="reset-badge" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.6 13.4a4 4 0 0 1 0-5.7l2.1-2.1a4 4 0 0 1 5.7 5.7l-1 1" />
                  <path d="M13.4 10.6a4 4 0 0 1 0 5.7l-2.1 2.1a4 4 0 0 1-5.7-5.7l1-1" />
                  <line x1="2" y1="2" x2="22" y2="22" />
                </svg>
              </span>
              <div className="reset-kicker">Password reset</div>
              <div className="reset-row reset-item">
                <span className="reset-title">Link no longer valid</span>
              </div>
              <div className="reset-row reset-note reset-item">
                <p>
                  {token
                    ? 'This reset link is invalid or has expired. Reset links last 24 hours and can only be used once.'
                    : 'This page needs a reset link from your email. Request a new one to continue.'}
                </p>
              </div>
              <div className="reset-row reset-item">
                <button className="reset-button" type="button" onClick={goToForgot}>
                  Request a new link
                </button>
              </div>
              <div className="reset-foot">
                <span className="reset-text" onClick={() => navigate(loginPath)}>
                  Back to login
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="reset-kicker">Password reset</div>
              <div className="reset-row reset-item">
                <span className="reset-title">Set a new password</span>
              </div>
              <div className="reset-row reset-note reset-item">
                <p>Choose a password you haven&rsquo;t used before. This link works once.</p>
              </div>

              {/* New password */}
              <div className="reset-row reset-item">
                <label htmlFor="reset-password">New Password</label>
              </div>
              <div className="reset-row reset-item">
                <div className="reset-input-wrapper">
                  <div className="left-part">
                    <input
                      id="reset-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="right-part">
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <EyeIcon open={showPassword} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Confirm password */}
              <div className="reset-row reset-item">
                <label htmlFor="reset-confirm">Confirm Password</label>
              </div>
              <div className="reset-row reset-item">
                <div className="reset-input-wrapper">
                  <div className="left-part">
                    <input
                      id="reset-confirm"
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="Re-enter new password"
                      autoComplete="new-password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="right-part">
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirm((v) => !v)}
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                      aria-pressed={showConfirm}
                      title={showConfirm ? 'Hide password' : 'Show password'}
                    >
                      <EyeIcon open={showConfirm} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Always-visible checklist — same rules the signup card uses */}
              <ul className="reset-checklist" aria-live="polite">
                {PASSWORD_RULES.map((rule) => {
                  const met = rule.test(password);
                  return (
                    <li key={rule.id} className={met ? 'pw-rule met' : 'pw-rule'}>
                      <span className="pw-rule-mark" aria-hidden="true">{met ? '✓' : '○'}</span>
                      <span className="pw-rule-text">{rule.label}</span>
                    </li>
                  );
                })}
                <li className={matches ? 'pw-rule met pw-rule--match' : 'pw-rule pw-rule--match'}>
                  <span className="pw-rule-mark" aria-hidden="true">{matches ? '✓' : '○'}</span>
                  <span className="pw-rule-text">Both passwords match</span>
                </li>
              </ul>

              <div className="reset-row reset-item">
                <button
                  className="reset-button"
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                >
                  {isSubmitting ? 'Resetting...' : 'Reset password'}
                </button>
              </div>
              <p className="reset-hint">Enabled once every rule above is met.</p>

              <div className="reset-foot">
                Remembered it?{' '}
                <span className="reset-text" onClick={() => navigate(loginPath)}>
                  Back to login
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <Notifier
        message={note.msg}
        type={note.type}
        signal={note.signal}
        durationMs={5000}
        onClose={() => setNote({ msg: '', type: 'info', signal: 0 })}
      />
    </div>
  );
};

export default ResetPassword;
